import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { object, string } from "zod";

import Button from "@busbud/design-system-components/dist/Button";
import Dialog from "@busbud/design-system-components/dist/Dialog";
import DialogActions from "@busbud/design-system-components/dist/DialogActions";
import DialogContent from "@busbud/design-system-components/dist/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle";
import Divider from "@busbud/design-system-components/dist/Divider";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Input } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import * as Tracking from "@app/tracking/purchase-confirmation-tracking";

import type { SubmitHandler } from "react-hook-form";

import { useStyles } from "./resend-confirmation-email.styles";
import { closeModal } from "../../../store/state/modals";

interface Props {
  user_email: string;
  sendEmail: (email: string) => Promise<void>;
  onEmailSent: (status: "success" | "error", new_email?: string) => void;
}

export const ResendConfirmationEmail: React.FC<Props> = ({
  user_email,
  sendEmail,
  onEmailSent
}) => {
  const { tracker, translator } = useAppContext();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const methods = useForm({
    defaultValues: { email: user_email },
    resolver: zodResolver(
      object({
        email: string().email()
      })
    )
  });
  const {
    register,
    handleSubmit,
    formState: { touchedFields, errors, isValid, isSubmitting }
  } = methods;

  const onSubmit: SubmitHandler<any> = async values => {
    tracker.track(Tracking.clickResendConfirmationEmail());
    sendEmail(values.email)
      .then(() => {
        tracker.track(Tracking.succeedResendConfirmationEmail());
        onEmailSent("success", values.email);
      })
      .catch(err => {
        tracker.track(Tracking.failResendConfirmationEmail());
        onEmailSent("error", err.code);
      })
      .finally(() => {
        dispatch(closeModal());
      });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Label size="lg" fontWeight="bold">
            {translator.t(
              "!purchase-confirmation.email-confirmation-edit-modal.title"
            )}
          </Label>
        </DialogTitle>
        <Divider />
        <Stack component={DialogContent} spacing={1} direction="column">
          <Input
            {...register("email")}
            type="email"
            labelText={translator.t(
              "!purchase-confirmation.email-confirmation-edit-modal.email-address"
            )}
            isInvalid={!!touchedFields.email && !!errors.email}
            data-testid="email-field"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- TODO: [fix-jsx-a11y]
            autoFocus
            className="w-full"
          />
          <Label
            size="sm"
            color="text.secondary.opaque"
            className={classes.legend}
          >
            {translator.t(
              "!purchase-confirmation.email-confirmation-edit-modal.tooltip"
            )}
          </Label>
        </Stack>
        <Divider />
        <DialogActions direction="horizontal">
          <Button
            size="md"
            onClick={() => {
              dispatch(closeModal());
            }}
            color="tertiary"
            disabled={isSubmitting}
            data-testid="close-button"
          >
            {translator.t("!modal.cta.close")}
          </Button>
          <Button
            size="md"
            id="submit"
            type="submit"
            data-testid="submit-button"
            disabled={isSubmitting || !isValid}
          >
            {isSubmitting
              ? translator.t(
                  "!purchase-confirmation.email-confirmation-edit-modal.loading"
                )
              : translator.t(
                  "!purchase-confirmation.email-confirmation-edit-modal.cta"
                )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
